import { Link } from "@tanstack/react-router";
import { useI18n } from "../../i18n/I18nProvider";
import type { Configuration } from "../../lib/api-types";
import { Button } from "../../components/ui/Button";
import { ErrorText } from "../../components/ui/feedback";
import { PageHeader } from "../../components/ui/PageHeader";
import { Status } from "../../components/ui/Status";
import { TabNav } from "../../components/ui/TabNav";
import { ExportButton } from "../export/ExportButton";
import { useConfigMutations } from "./mutations";

export interface ConfigHeaderProps {
  workspaceId: string;
  configuration: Configuration;
  tab: "slots" | "keys";
}

/** Lineup header: back link, the configuration name in display caps, lock control, export and tabs. */
export function ConfigHeader({ workspaceId, configuration, tab }: ConfigHeaderProps) {
  const { t } = useI18n();
  const mutations = useConfigMutations(workspaceId);
  const locked = configuration.locked;
  const busy = mutations.update.isPending;

  return (
    <div className="space-y-2">
      <Link
        to="/app/$workspaceId/configurations"
        params={{ workspaceId }}
        className="inline-flex min-h-11 items-center text-note font-semibold text-blue hover:underline"
      >
        ← {t("editor.back")}
      </Link>
      <PageHeader
        title={configuration.name}
        actions={
          <>
            <Status tone={locked ? "live" : "idle"}>
              {locked ? t("common.locked") : t("configs.open")}
            </Status>
            <Button
              disabled={busy}
              onClick={() =>
                mutations.update.mutate({
                  configurationId: configuration.id,
                  patch: { locked: !locked },
                })
              }
            >
              {locked ? t("configs.unlock") : t("configs.lockForGame")}
            </Button>
            <ExportButton
              workspaceId={workspaceId}
              configurationId={configuration.id}
              configurationName={configuration.name}
            />
          </>
        }
      />
      <ErrorText error={mutations.update.error} />
      <TabNav
        ariaLabel={configuration.name}
        items={[
          {
            key: "slots",
            label: t("tabs.slots"),
            href: "",
            active: tab === "slots",
          },
          {
            key: "keys",
            label: t("tabs.keys"),
            href: "",
            active: tab === "keys",
          },
        ]}
        renderLink={(item, className) =>
          item.key === "slots" ? (
            <Link
              to="/app/$workspaceId/configurations/$configurationId"
              params={{ workspaceId, configurationId: configuration.id }}
              className={className}
            >
              {item.label}
            </Link>
          ) : (
            <Link
              to="/app/$workspaceId/configurations/$configurationId/keys"
              params={{ workspaceId, configurationId: configuration.id }}
              className={className}
            >
              {item.label}
            </Link>
          )
        }
      />
    </div>
  );
}
